// Shared config for the global, role/org/page-aware left side navigation.
// Also doubles as the source of truth for "Pin Shortcuts" destination options
// so the sidebar and the shortcut picker never drift out of sync.
import {
  IconHome2,
  IconBooks,
  IconEye,
  IconDeviceVisionPro,
  IconRobot,
  IconRoute,
  IconChartBar,
  IconClipboardCheck,
  IconLanguage,
  IconMessageChatbot,
  IconBook2,
  IconVideo,
  IconMicrophone,
  IconPencil,
  IconFolder,
  IconListDetails,
  IconId,
  IconBolt,
  IconDatabase,
  IconFileText,
  IconReportAnalytics,
  IconCertificate,
  IconShieldLock,
  IconMailbox,
  IconBriefcase,
  IconBuildingCommunity,
  IconCreditCard,
  IconSettings,
  IconPhone,
  IconLogout,
} from '@tabler/icons-react'

// Routes that render the AppSideNav today. Extend this set/list as later
// phases opt more pages into the shared sidebar.
// - Exact routes: the sidebar only activates on that precise pathname.
// - Prefix routes: the sidebar activates on that pathname and every
//   sub-route beneath it (e.g. a section with its own set of sub-pages).
// - Matchers: for sections that mix sidebar and non-sidebar (fullscreen)
//   routes under the same prefix, e.g. the assessment engine's fullscreen
//   runner/editor pages.
const SIDE_NAV_EXACT_ROUTES = new Set([
  '/dashboard',
  '/courses',
  '/visualize',
  '/my-activity',
  '/dashboard/xr',
  '/dashboard/xr/ai-avatar',
  '/admin',
  '/messages',
  '/learning-delivery',
  '/admin/org-branding',
  '/profile',
  '/contact',
  '/payment-history',
  // Learning & Delivery → Quick Actions / create-course destinations
  '/course-editor',
  '/course-history',
  '/quiz-results',
  '/admin/notifications',
  '/admin/create-course',
  '/create-xr-course',
  '/create-virtual-world',
  '/admin/usage-tracker',
  '/admin/sso-config',
  '/admin/integrations',
  '/admin/global-role-catalog',
  '/revenue-tracker',
  '/parent_dashboard',
])

// Prefix families reached from Learning & Delivery (and the Course Editor
// workflow that page launches). Keep immersive/fullscreen viewers out via
// SIDE_NAV_ROUTE_MATCHERS below where needed.
const SIDE_NAV_ROUTE_PREFIXES = [
  '/language-lab',
  '/adaptive',
  '/admin/course-exports',
  '/admin/edit-course',
  '/edit-xr-course',
  '/edit-virtual-world',
  '/content-editor',
  '/certificate-studio',
  '/admin/ar-assets',
]

function matchesRouteFamily(pathname, prefix) {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

// The assessment pack runner/editor/psychometric-editor are intentionally
// fullscreen (no sidebar) even though they live under /assessment-engine —
// keep this in sync with the "fullscreen flows (no sidebar)" routes in App.jsx.
const ASSESSMENT_FULLSCREEN_PATTERNS = [
  /^\/assessment-engine\/pack\/[^/]+\/run$/,
  /^\/assessment-engine\/pack\/[^/]+\/edit$/,
  /^\/assessment-engine\/pack\/[^/]+\/psychometric\/edit$/,
]

function isAssessmentHubRoute(pathname) {
  if (ASSESSMENT_FULLSCREEN_PATTERNS.some((pattern) => pattern.test(pathname))) return false
  return matchesRouteFamily(pathname, '/assessments') || matchesRouteFamily(pathname, '/assessment-engine')
}

// AR Classroom list + asset detail keep the sidebar; the VR / Advanced AR
// viewers are fullscreen immersive experiences and should not.
const AR_CLASSROOM_IMMERSIVE_PATTERNS = [
  /^\/ar-classroom\/[^/]+\/vr$/,
  /^\/ar-classroom\/[^/]+\/advanced-ar$/,
]

function isArClassroomSideNavRoute(pathname) {
  if (AR_CLASSROOM_IMMERSIVE_PATTERNS.some((pattern) => pattern.test(pathname))) return false
  return matchesRouteFamily(pathname, '/ar-classroom')
}

// Course detail: /courses/:courseId — not the marketplace (/courses), catalog
// (/courses/all), or landing (/courses/:id/landing) pages.
function isCourseDetailSideNavRoute(pathname) {
  const match = pathname.match(/^\/courses\/([^/]+)$/)
  if (!match) return false
  return match[1] !== 'all'
}

const SIDE_NAV_ROUTE_MATCHERS = [
  isAssessmentHubRoute,
  isArClassroomSideNavRoute,
  isCourseDetailSideNavRoute,
]

// Dispatched on window by the navbar menu button to open/close the side nav
// drawer on phones, where AppSideNav's floating opener is hidden.
export const APP_SIDENAV_TOGGLE_EVENT = 'app-sidenav:toggle'

export function hasAppSideNav(pathname) {
  if (SIDE_NAV_EXACT_ROUTES.has(pathname)) return true
  if (SIDE_NAV_ROUTE_PREFIXES.some((prefix) => matchesRouteFamily(pathname, prefix))) return true
  return SIDE_NAV_ROUTE_MATCHERS.some((matcher) => matcher(pathname))
}

// Page-aware background applied at the AppSideNavLayout level so the sidebar
// and the page content always share one continuous background instead of the
// sidebar looking like a separate opaque panel while scrolling. Keyed by
// pathname (exact or prefix, matching hasAppSideNav's route families); add
// an entry here whenever a new page adopts a distinct tint. (The fullscreen
// assessment-engine routes never mount AppSideNavLayout, so no exclusion is
// needed here the way it is in hasAppSideNav.)
const DEFAULT_TINTED_BACKGROUND =
  'radial-gradient(circle at 8% 8%, rgba(96,165,250,0.09), transparent 24%), ' +
  'radial-gradient(circle at 92% 10%, rgba(244,114,182,0.08), transparent 22%), ' +
  'linear-gradient(180deg, #f7fbff 0%, #fff9fc 48%, #f7fbff 100%)'

// Matches the slate -> blue -> indigo page gradient used by LearningDelivery
// and OrgBranding (Tailwind `bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100`).
const SLATE_BLUE_INDIGO_BACKGROUND = 'linear-gradient(to bottom right, #f8fafc, #eff6ff, #e0e7ff)'
// Matches profile.jsx's softer gradient (`from-slate-50 via-indigo-50/30 to-blue-50`).
const SLATE_INDIGO_SOFT_BACKGROUND = 'linear-gradient(to bottom right, #f8fafc, rgba(238,242,255,0.5), #eff6ff)'

// Soft blue page wash used by Create Course / Edit XR Course.
const SOFT_BLUE_STUDIO_BACKGROUND =
  'radial-gradient(circle at 8% -10%, rgba(56,189,248,0.18), transparent 42%), ' +
  'radial-gradient(circle at 92% 10%, rgba(99,102,241,0.16), transparent 35%), ' +
  'linear-gradient(180deg, #f8fbff 0%, #eef4ff 45%, #f7faff 100%)'
const CREATE_COURSE_BACKGROUND = 'linear-gradient(180deg, #eef5ff, #f9fbff)'
const GRAY_50_BACKGROUND = '#f9fafb'
const SLATE_50_BACKGROUND = '#f8fafc'
const SLATE_100_BACKGROUND = '#f1f5f9'
// ParentDashboard's page wash (`linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)`).
const PARENT_DASHBOARD_BACKGROUND = 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
// AdminGlobalRoleCatalog (`from-slate-50 via-indigo-50/40 to-white`).
const GLOBAL_ROLE_CATALOG_BACKGROUND = 'linear-gradient(to bottom right, #f8fafc, rgba(238,242,255,0.4), #ffffff)'

export const PAGE_BACKGROUND_STYLES = {
  '/dashboard': DEFAULT_TINTED_BACKGROUND,
  '/courses': DEFAULT_TINTED_BACKGROUND,
  '/language-lab': DEFAULT_TINTED_BACKGROUND,
  '/assessments': DEFAULT_TINTED_BACKGROUND,
  '/assessment-engine': DEFAULT_TINTED_BACKGROUND,
  '/visualize': DEFAULT_TINTED_BACKGROUND,
  '/adaptive': DEFAULT_TINTED_BACKGROUND,
  '/my-activity': DEFAULT_TINTED_BACKGROUND,
  // XR Dashboard paints its own full-viewport dark scene, but this keeps the
  // sidebar column (which is transparent there) and any pre-paint flash
  // consistent with the page's dark theme instead of the app's default white.
  '/dashboard/xr': '#020617',
  '/dashboard/xr/ai-avatar': DEFAULT_TINTED_BACKGROUND,
  '/learning-delivery': SLATE_BLUE_INDIGO_BACKGROUND,
  '/admin/org-branding': SLATE_BLUE_INDIGO_BACKGROUND,
  '/profile': SLATE_INDIGO_SOFT_BACKGROUND,
  '/payment-history': SLATE_INDIGO_SOFT_BACKGROUND,
  // Learning & Delivery destinations
  '/ar-classroom': SLATE_BLUE_INDIGO_BACKGROUND,
  '/course-editor': GRAY_50_BACKGROUND,
  '/course-history': SLATE_50_BACKGROUND,
  '/quiz-results': GRAY_50_BACKGROUND,
  '/admin/course-exports': SLATE_50_BACKGROUND,
  '/admin/notifications': SLATE_BLUE_INDIGO_BACKGROUND,
  '/admin/create-course': CREATE_COURSE_BACKGROUND,
  '/create-xr-course': SOFT_BLUE_STUDIO_BACKGROUND,
  '/create-virtual-world': '#020617',
  '/admin/edit-course': GRAY_50_BACKGROUND,
  '/edit-xr-course': SOFT_BLUE_STUDIO_BACKGROUND,
  '/edit-virtual-world': '#020617',
  '/content-editor': GRAY_50_BACKGROUND,
  '/certificate-studio': SLATE_100_BACKGROUND,
  // Matches UsageTrackerPage's own `bg-slate-50`.
  '/admin/usage-tracker': SLATE_50_BACKGROUND,
  '/admin/sso-config': SLATE_50_BACKGROUND,
  '/admin/ar-assets': SLATE_BLUE_INDIGO_BACKGROUND,
  '/admin/integrations': GRAY_50_BACKGROUND,
  '/admin/global-role-catalog': GLOBAL_ROLE_CATALOG_BACKGROUND,
  '/revenue-tracker': GRAY_50_BACKGROUND,
  '/parent_dashboard': PARENT_DASHBOARD_BACKGROUND,
}

const DEFAULT_PAGE_BACKGROUND = '#ffffff'

export function getPageBackground(pathname) {
  if (PAGE_BACKGROUND_STYLES[pathname]) return PAGE_BACKGROUND_STYLES[pathname]
  // Course detail uses gray-50; don't inherit the marketplace tint from /courses.
  if (isCourseDetailSideNavRoute(pathname)) return GRAY_50_BACKGROUND
  const prefixMatch = Object.keys(PAGE_BACKGROUND_STYLES).find((prefix) => matchesRouteFamily(pathname, prefix))
  return prefixMatch ? PAGE_BACKGROUND_STYLES[prefixMatch] : DEFAULT_PAGE_BACKGROUND
}

// Every navigable "destination" the sidebar/shortcut-picker can offer.
// `visibilityKey`, when present, is looked up on the ctx object passed to
// isDestinationVisible/getPrimaryNavItems/getPinnableDestinations; missing
// keys default to visible so callers only need to supply the checks they can
// accurately compute.
export const DESTINATION_CATALOG = {
  dashboard: {
    key: 'dashboard',
    label: 'Dashboard',
    description: 'Your learning home',
    to: '/dashboard',
    icon: IconHome2,
    exact: true,
  },
  catalog: {
    key: 'catalog',
    label: 'Course Catalog',
    description: 'Explore and enroll',
    to: '/courses',
    icon: IconBooks,
    exact: true,
    visibilityKey: 'showCourseCatalogButton',
  },
  visualize: {
    key: 'visualize',
    label: 'Visualize Books',
    description: 'Generate visuals',
    to: '/visualize',
    icon: IconEye,
    visibilityKey: 'canShowVisualizeButton',
  },
  xr: {
    key: 'xr',
    label: 'Launch in XR',
    description: 'Immersive mode',
    to: '/dashboard/xr',
    icon: IconDeviceVisionPro,
    // Exact: AI Avatar lives under /dashboard/xr/ai-avatar and must not
    // light up this row via the usual prefix match.
    exact: true,
    visibilityKey: 'canShowXrButton',
  },
  'ai-avatar': {
    key: 'ai-avatar',
    label: 'AI Avatar',
    description: 'Talk to your AI tutor',
    to: '/dashboard/xr/ai-avatar',
    icon: IconRobot,
    visibilityKey: 'canShowAiAvatarButton',
  },
  palj: {
    key: 'palj',
    label: 'Adaptive Learning',
    description: 'Tailored for you',
    to: '/adaptive',
    icon: IconRoute,
    visibilityKey: 'canShowPaljButton',
  },
  'language-lab': {
    key: 'language-lab',
    label: 'Language Lab',
    description: 'Practice speaking & writing',
    to: '/language-lab',
    icon: IconLanguage,
    visibilityKey: 'canAccessLanguageLab',
  },
  progress: {
    key: 'progress',
    label: 'My Activity',
    description: 'Track learning progress',
    to: '/my-activity',
    icon: IconChartBar,
  },
  assessments: {
    key: 'assessments',
    label: 'Assessments',
    description: 'Browse assessments',
    to: '/assessments',
    icon: IconClipboardCheck,
    visibilityKey: 'showAssessmentsButton',
  },
}

// Single canonical order for the primary nav. This intentionally never
// changes based on the active page — only which row is highlighted as
// "active" changes when you navigate. If a page needs extra, page-specific
// links, add them as nested child rows via PRIMARY_ITEM_CHILDREN below
// (rendered under their parent while that page is active) instead of
// reordering or duplicating this list.
const PRIMARY_ORDER = [
  'dashboard',
  'catalog',
  'palj',
  'language-lab',
  'ai-avatar',
  'xr',
  'assessments',
  'visualize',
  'progress',
]

// Child rows nested under a primary item, keyed by that item's key. They
// render indented beneath their parent only while a page within that
// section is active (see AppSideNav's isItemActive check), instead of
// reordering or duplicating the shared top-level list above. Sections that
// have their own family of sub-pages (like Language Lab, which used to run
// its own standalone sidebar) declare their sub-nav here.
const PRIMARY_ITEM_CHILDREN = {
  'language-lab': [
    { key: 'll-home', label: 'Overview', to: '/language-lab', icon: IconHome2, exact: true },
    {
      key: 'll-tutor',
      label: 'AI Tutor Lab',
      to: '/language-lab/tutor',
      icon: IconMessageChatbot,
      visibilityKey: 'canShowLanguageLabTutor',
    },
    { key: 'll-vocabulary', label: 'Vocabulary', to: '/language-lab/vocabulary', icon: IconBook2 },
    { key: 'll-mouth-movements', label: 'Mouth Movements', to: '/language-lab/mouth-movements', icon: IconVideo },
    { key: 'll-practice', label: 'Practice Lab', to: '/language-lab/practice', icon: IconMicrophone },
    {
      key: 'll-writing',
      label: 'Writing Lab',
      to: '/language-lab/writing',
      icon: IconPencil,
      visibilityKey: 'canShowLanguageLabWriting',
    },
    { key: 'll-resources', label: 'Resources', to: '/language-lab/resources', icon: IconFolder },
  ],
  assessments: [
    { key: 'as-browse', label: 'Browse Assessments', to: '/assessments', icon: IconListDetails, exact: true },
    { key: 'as-passport', label: 'My Passport', to: '/assessments/passport', icon: IconId },
    {
      key: 'as-engine',
      label: 'Assessment Engine',
      to: '/assessment-engine',
      icon: IconBolt,
      exact: true,
      visibilityKey: 'isAssessmentAdmin',
      adminOnly: true,
    },
    {
      key: 'as-question-bank',
      label: 'Question Bank',
      to: '/assessment-engine/question-bank',
      icon: IconDatabase,
      exact: true,
      visibilityKey: 'isAssessmentAdmin',
      adminOnly: true,
    },
    {
      key: 'as-paper-designer',
      label: 'Paper Designer',
      to: '/assessment-engine/paper-designer',
      icon: IconFileText,
      visibilityKey: 'isAssessmentAdmin',
      adminOnly: true,
    },
    {
      key: 'as-reports',
      label: 'Reports',
      to: '/assessment-engine/reports',
      icon: IconReportAnalytics,
      visibilityKey: 'isAssessmentAdmin',
      adminOnly: true,
    },
    {
      key: 'as-certificates',
      label: 'Certificates',
      to: '/assessment-engine/certificates',
      icon: IconCertificate,
      visibilityKey: 'isAssessmentAdmin',
      adminOnly: true,
    },
  ],
}

// Destinations a learner may pin as a shortcut (everything except the
// "Dashboard" home link itself, which is always the first primary nav item).
export const PINNABLE_DESTINATION_KEYS = PRIMARY_ORDER.filter((key) => key !== 'dashboard')

function isDestinationVisible(destination, ctx) {
  if (!destination) return false
  if (!destination.visibilityKey) return true
  return ctx?.[destination.visibilityKey] !== false
}

// Primary (top) section of the sidebar. The order and item set are always
// the same regardless of the active page — only the active row (and any
// nested children it exposes) changes as you navigate.
export function getPrimaryNavItems(ctx = {}) {
  return PRIMARY_ORDER
    .map((key) => DESTINATION_CATALOG[key])
    .filter((destination) => isDestinationVisible(destination, ctx))
    .map((destination) => {
      const children = PRIMARY_ITEM_CHILDREN[destination.key]
      if (!children) return destination
      const visibleChildren = children.filter((child) => isDestinationVisible(child, ctx))
      return visibleChildren.length ? { ...destination, children: visibleChildren } : destination
    })
}

// Used by the "Pin Shortcuts" picker so it only offers destinations the
// learner can actually reach.
export function getPinnableDestinations(ctx = {}) {
  return PINNABLE_DESTINATION_KEYS
    .map((key) => DESTINATION_CATALOG[key])
    .filter((destination) => isDestinationVisible(destination, ctx))
}

// Fixed bottom section: role/org-gated admin & account links, mirroring the
// same conditions the profile dropdown in Navbar.jsx uses today.
export function getSecondaryNavItems(ctx = {}) {
  const { isSuperAdmin, isAdmin, isCreator, isTrainer, bypassPaidPlans } = ctx
  const items = []

  if (isSuperAdmin) {
    items.push({ key: 'admin-dashboard', label: 'Admin Dashboard', to: '/admin', icon: IconShieldLock, tone: 'admin', adminOnly: true })
  }
  if (isAdmin || isSuperAdmin) {
    items.push({ key: 'messages', label: 'Messages Inbox', to: '/messages', icon: IconMailbox, tone: 'admin', adminOnly: true })
  }
  if (isAdmin || isCreator || isTrainer) {
    items.push({ key: 'learning-delivery', label: 'Learning & Delivery', to: '/learning-delivery', icon: IconBriefcase, tone: 'admin', adminOnly: true })
  }
  if (isAdmin || isCreator) {
    items.push({ key: 'org-control', label: 'Organization Control', to: '/admin/org-branding', icon: IconBuildingCommunity, tone: 'admin', adminOnly: true })
  }
  items.push({ key: 'settings', label: 'Settings', to: '/profile', icon: IconSettings })
  if (!bypassPaidPlans) {
    items.push({ key: 'plans', label: 'Plans & Billing', to: '/plans', icon: IconCreditCard })
  }
  items.push({ key: 'contact', label: 'Contact', to: '/contact', icon: IconPhone })
  items.push({ key: 'logout', label: 'Logout', icon: IconLogout, tone: 'danger', action: 'logout' })

  return items
}
